/**
 * Search Bar with Autocomplete
 * 
 * Shows suggestions from:
 * - Templates (prompts)
 * - Profiles  
 * - Dashboard pages
 * 
 * Per design.md: Use semantic tokens, lucide icons only
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, FileText, User, LayoutDashboard, FolderOpen, X } from 'lucide-react';
import { Input, cn } from '@uprosal/ui';
import { useQuery } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc } from 'convex/_generated/dataModel';
import { useNavigate } from 'react-router-dom';

interface SearchResult {
    id: string;
    type: 'template' | 'profile' | 'page';
    title: string;
    subtitle?: string;
    path: string;
}

const STATIC_PAGES: SearchResult[] = [
    { id: 'dashboard', type: 'page', title: 'Dashboard', subtitle: 'Overview & stats', path: '/dashboard' },
    { id: 'templates', type: 'page', title: 'Templates', subtitle: 'Manage prompts', path: '/templates' },
    { id: 'profiles', type: 'page', title: 'Profiles', subtitle: 'Upwork profiles', path: '/profiles' },
    { id: 'proof', type: 'page', title: 'Proof Library', subtitle: 'Portfolio items', path: '/proof' },
    { id: 'analytics', type: 'page', title: 'Analytics', subtitle: 'Usage statistics', path: '/analytics' },
    { id: 'history', type: 'page', title: 'History', subtitle: 'Proposal drafts', path: '/history' },
    { id: 'settings', type: 'page', title: 'Settings', subtitle: 'Account preferences', path: '/settings' },
    { id: 'billing', type: 'page', title: 'Billing', subtitle: 'Plan & usage', path: '/billing' },
    { id: 'team', type: 'page', title: 'Team', subtitle: 'Manage members', path: '/team' },
];

const typeIcons = {
    template: FileText,
    profile: User,
    page: LayoutDashboard,
};

export function SearchBar() {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch templates (prompts) for search
    const prompts = useQuery(api.queries.prompts.list, {}) as Doc<'prompts'>[] | undefined;
    const profiles = useQuery(api.queries.profiles.listConnected) as Doc<'connectedProfiles'>[] | undefined;

    // Build search results
    const results = useMemo<SearchResult[]>(() => {
        if (!query.trim()) return [];

        const lowerQuery = query.toLowerCase();
        const matches: SearchResult[] = [];

        // Search pages
        STATIC_PAGES.forEach(page => {
            if (page.title.toLowerCase().includes(lowerQuery) ||
                page.subtitle?.toLowerCase().includes(lowerQuery)) {
                matches.push(page);
            }
        });

        // Search templates (prompts)
        prompts?.forEach((t) => {
            if (t.name.toLowerCase().includes(lowerQuery) ||
                t.category?.toLowerCase().includes(lowerQuery)) {
                matches.push({
                    id: t._id,
                    type: 'template',
                    title: t.name,
                    subtitle: t.category || 'Template',
                    path: `/templates?highlight=${t._id}`,
                });
            }
        });

        // Search profiles (use profileLabel as name, platform as 'Upwork')
        profiles?.forEach((p) => {
            const profileName = p.profileLabel || p.title || 'Upwork Profile';
            if (profileName.toLowerCase().includes(lowerQuery) ||
                'upwork'.includes(lowerQuery)) {
                matches.push({
                    id: p._id,
                    type: 'profile',
                    title: profileName,
                    subtitle: 'Upwork',
                    path: `/profiles?highlight=${p._id}`,
                });
            }
        });

        return matches.slice(0, 8); // Limit to 8 results
    }, [query, prompts, profiles]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedIndex(i => Math.min(i + 1, results.length - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedIndex(i => Math.max(i - 1, 0));
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (results[selectedIndex]) {
                        handleSelect(results[selectedIndex]);
                    }
                    break;
                case 'Escape':
                    setIsOpen(false);
                    inputRef.current?.blur();
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, selectedIndex, results]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
                inputRef.current && !inputRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (result: SearchResult) => {
        navigate(result.path);
        setQuery('');
        setIsOpen(false);
    };

    return (
        <div className="relative w-full max-w-md">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    ref={inputRef}
                    type="text"
                    placeholder="Search jobs, prompts..."
                    className="w-full pl-10 pr-8"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                        setSelectedIndex(0);
                    }}
                    onFocus={() => query && setIsOpen(true)}
                />
                {query && (
                    <button
                        onClick={() => {
                            setQuery('');
                            setIsOpen(false);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Dropdown */}
            {isOpen && results.length > 0 && (
                <div
                    ref={dropdownRef}
                    className="absolute top-full mt-2 w-full rounded-lg border border-border bg-popover shadow-lg py-1 z-50"
                >
                    {results.map((result, index) => {
                        const Icon = typeIcons[result.type];
                        return (
                            <button
                                key={result.id}
                                onClick={() => handleSelect(result)}
                                className={cn(
                                    'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                                    index === selectedIndex
                                        ? 'bg-muted'
                                        : 'hover:bg-muted/50'
                                )}
                            >
                                <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-foreground truncate">
                                        {result.title}
                                    </p>
                                    {result.subtitle && (
                                        <p className="text-xs text-muted-foreground truncate">
                                            {result.subtitle}
                                        </p>
                                    )}
                                </div>
                                <span className="text-xs text-muted-foreground/70 capitalize shrink-0">
                                    {result.type}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* No results */}
            {isOpen && query && results.length === 0 && (
                <div
                    ref={dropdownRef}
                    className="absolute top-full mt-2 w-full rounded-lg border border-border bg-popover shadow-lg py-4 z-50 text-center"
                >
                    <FolderOpen className="w-6 h-6 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No results for "{query}"</p>
                </div>
            )}
        </div>
    );
}
